<template>
    <div class="workflow-container">
        <div class="workflow-header">
            <h1 class="workflow-title">{{ $t("task.allWorkflow") }}</h1>
            <div style="display:flex; gap:10px">
                <div class="view-toggle-btn" @click="$feature.beforeCreateTaskWorkflow(createWorkflow)">
                    <i class="material-icons">add_circle</i>
                    <span>{{ $t("task.createworkflow") }}</span>
                </div>
                <button v-if="!isTableView" class="view-toggle-btn" :class="{ 'drag-active': isDragEnabled }" @click="toggleDragMode">
                    <i class="material-icons">{{ isDragEnabled ? "check_circle" : "swap_vert" }}</i>
                    {{ isDragEnabled ? $t("task.editDragActive") : $t("task.editSortOrder") }}
                </button>
                <button class="view-toggle-btn" @click="toggleView">
                    <i class="material-icons">{{ isTableView ? "grid_view" : "list" }}</i>
                    {{ isTableView ? $t("message.gridView") : $t("message.tableView") }}
                </button>
            </div>
        </div>

        <div v-if="isTableView" class="table-view-container">
            <div class="table-wrapper">
                <table class="workflow-table">
                    <thead>
                    <tr>
                        <th>{{ $t("task.workflowTitle") }}</th>
                        <th class="actions-column">{{ $t("task.workflowActions") }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="workflow in getWorkflows()" :key="workflow._id">
                        <td class="title-column">{{ workflow.title }}</td>
                        <td class="actions-column">
                            <div class="action-buttons">
                                <button class="action-btn open" @click="openWorkflow(workflow._id)">
                                    <i class="material-icons">open_in_new</i>
                                    <span>{{ $t("task.view") }}</span>
                                </button>
                                <button v-if="taskDetailPermission()"
                                        class="action-btn edit" @click="editWorkFlow(workflow._id)">
                                    <i class="material-icons">edit</i>
                                    <span>{{ $t("message.edit") }}</span>
                                </button>
                                <button v-if="taskDetailPermission()"
                                        class="action-btn delete" @click="deleteWorkflowFunc(workflow)">
                                    <i class="material-icons">delete</i>
                                    <span>{{ $t("chatbotv2.delete") }}</span>
                                </button>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-else class="grid-view-container">
            <div class="draggable-container">
                <draggable v-model="localWorkflows" @end="updateWorkflowsOrder" class="workflow-drag" :disabled="!isDragEnabled">
                    <div v-for="workflow in localWorkflows"
                         :key="workflow._id"
                         class="workflow-card"
                         :class="{ 'drag-mode': isDragEnabled }"
                         @click="!isDragEnabled && openWorkflow(workflow._id)">
                        <div class="workflow-card-content">
                            <WorkflowIcon class="workflow-icon" />
                            <h3 class="workflow-card-title" v-tippy="{ content: workflow.title, placement: 'top' }">
                                {{ workflow.title.length > 25 ? workflow.title.substring(0, 25) + "..." : workflow.title
                                }}
                            </h3>
                        </div>
                        <div class="workflow-card-actions" v-if="taskDetailPermission()">
                            <a-dropdown :trigger="['click']" placement="bottomRight">
                                <button class="more-btn" @click.stop>
                                    <i class="material-icons">more_horiz</i>
                                </button>
                                <template #overlay>
                                    <a-menu>
                                        <a-menu-item @click="editWorkFlow(workflow._id)" style="display:flex; gap: 5px">
                                            <i class="material-icons">edit</i>
                                            <span>{{ $t("message.edit") }}</span>
                                        </a-menu-item>
                                        <a-menu-item @click="copyWorkFlowId(workflow._id)" style="display:flex; gap: 5px">
                                            <i class="material-icons">content_copy</i>
                                            <span>WorkFlow ID Kopyala</span>
                                        </a-menu-item>
                                        <a-menu-item @click="deleteWorkflowFunc(workflow)"
                                                     style="display:flex; gap: 5px">
                                            <i class="material-icons">delete</i>
                                            <span>{{ $t("chatbotv2.delete") }}</span>
                                        </a-menu-item>
                                    </a-menu>
                                </template>
                            </a-dropdown>
                        </div>
                    </div>
                </draggable>
            </div>
        </div>

        <DeleteModal
            v-if="deleteModal"
            :description="$t('task.deleteWorkflow')"
            :btnCancelText="$t('message.cancel')"
            :btnoktext="$t('message.delete')"
            @cancel="deleteModal = false"
            @confirm="softDeleteWorkflow"
        />
    </div>
</template>

<script>
import draggable from "vuedraggable";
import WorkflowIcon from "../../views/Pages/Chatbot/pages/integrations/svg/WorkflowIcon.vue";
import WhatsappIcon from "../../views/Pages/Chatbot/pages/integrations/svg/WhatsappIcon.vue";
import Modal2 from "../common/Modal2.vue";
import NewMultiWhatsappChannel from "../../views/Pages/WhatsappV1/NewMultiWhatsappChannel.vue";
import AppBreadCrumb from "../common/AppBreadCrumb.vue";
import EditMultiWhatsappChannel from "../../views/Pages/WhatsappV1/UpdateDetail.vue";
import UpgradeNeeded from "../billing/UpgradeNeeded.vue";
import Templates from "../../views/Pages/Chatbot/pages/templates/Templates.vue";
import TelegramIcon from "../../views/Pages/Chatbot/pages/integrations/svg/TelegramIcon.vue";
import SupsisIcon from "../../views/Pages/Chatbot/pages/integrations/svg/SupsisIcon.vue";
import TwitterIcon from "../../views/Pages/Chatbot/pages/integrations/svg/TwitterIcon.vue";
import SlackIcon from "../../views/Pages/Chatbot/pages/integrations/svg/SlackIcon.vue";
import ShopifyIcon from "../fast-initialize/svg/ShopifyIcon.vue";
import FacebookIcon from "../../views/Pages/Chatbot/pages/integrations/svg/FacebookIcon.vue";
import SkypeIcon from "../../views/Pages/Chatbot/pages/integrations/svg/SkypeIcon.vue";
import WebhookIcon from "../../views/Pages/Chatbot/pages/integrations/svg/WebhookIcon.vue";
import InstagramIcon from "../../views/Pages/Chatbot/pages/integrations/svg/InstagramIcon.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import DeleteModal from "../../views/Pages/Tasks/components/DeleteModal.vue";
import { PERMISSION_TYPES } from "../../language/permissionTypes";

export default {
    name: "Workflow",
    components: {
        Templates,
        NewMultiWhatsappChannel,
        WhatsappIcon,
        TelegramIcon,
        SupsisIcon,
        TwitterIcon,
        SlackIcon,
        ShopifyIcon,
        FacebookIcon,
        SkypeIcon,
        WebhookIcon,
        InstagramIcon,
        Modal2,
        EditMultiWhatsappChannel,
        AppBreadCrumb,
        UpgradeNeeded,
        WorkflowIcon,
        DeleteModal,
        draggable,
    },
    data() {
        return {
            showCreateForm: false,
            editShowCreateForm: false,
            updatingChannel: null,
            deleteModal: false,
            maxChannelCount: 0,
            deleteChannelId: null,
            showUpgrade: false,
            deleteWorkflowId: null,
            isTableView: false,
            localWorkflows: [],
            isDragEnabled: false,
            firstWorkflowLoaded: false,
        };
    },
    computed: {
        PERMISSION_TYPES() {
            return PERMISSION_TYPES;
        },
        ...mapState({
            user: (state) => state.auth.user,
            isRoot: (state) => state.auth && state.auth.user && state.auth.user.role && state.auth.user.role.isRoot,
            isAdmin: (state) => state.auth && state.auth.adminUser != null,
            workflows: (state) =>
                state.tasks.workflows.reduce((acc, workflow) => {
                    acc[workflow._id] = workflow;
                    return acc;
                }, {}),
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
    },
    // watch: {
    //     workflows: {
    //         handler(newWorkflows) {
    //             this.localWorkflows = Object.values(newWorkflows);
    //         },
    //         immediate: true,
    //     },
    // },
    methods: {
        ...mapActions({
            addNewWindow: "app/addNewWindow",
            deleteWorkflow: "tasks/deleteWorkflow",
            deleteChannel: "channel/deleteChannel",
            updateWorkflowOrder: "tasks/updateWorkflowOrder",
            getWorkFlows: "tasks/getWorkFlows",
        }),
        deleteWorkflowFunc(data) {
            this.deleteModal = true;
            this.deleteWorkflowId = data._id;
        },
        softDeleteWorkflow() {
            this.deleteWorkflow(this.deleteWorkflowId).then(() => {
                this.deleteModal = false;
            });
        },
        createWorkflow() {
            this.addNewWindow({
                type: "create-workflow",
            });
        },
        openWorkflow(id) {
            this.$emit("selected-workflow", id);
        },
        editWorkFlow(id) {
            this.$router.push(`/tasks/workflow/${id}/detail`);
        },
        copyWorkFlowId(id) {
            navigator.clipboard.writeText(id).then(() => {
                this.$message.success("WorkflowId kopyalandı");
            }).catch(() => {
                this.$message.error("WorkflowId kopyalanamadı");
            });
        },
        getWorkflows() {
            return Object.keys(this.workflows)
                .map((workflow) => this.workflows[workflow])
                .filter((workflow) => {
                    if (this.isRoot || this.isAdmin) {
                        return true;
                    }
                    const visibility = workflow.visibility || {};
                    const users = visibility.users || [];
                    const departments = visibility.departments || [];
                    const userSubjects = this.user.subjects || [];
                    const userId = this.user._id || null;

                    if (users.includes(userId) && departments.length === 0) {
                        return true;
                    } else if (departments.some((dept) => userSubjects.includes(dept)) && users.length === 0) {
                        return true;
                    } else if (users.includes(userId) || departments.some((dept) => userSubjects.includes(dept))) {
                        return true;
                    } else if (users.length === 0 && departments.length === 0) {
                        return true;
                    }
                    return false;
                })
                .map((workflow) => {
                    return {
                        ...workflow,
                        title: workflow.title,
                    };
                });
        },
        toggleView() {
            this.isTableView = !this.isTableView;
            this.isDragEnabled = false;
        },
        toggleDragMode() {
            this.isDragEnabled = !this.isDragEnabled;
        },
        taskDetailPermission() {
            return [
                PERMISSION_TYPES["task.detailRead"].tr.name,
            ].some((permission) => this.checkPermission(permission));
        },
        updateWorkflowsOrder() {
            const updatedWorkflows = this.localWorkflows.map((workflow, index) => ({
                ...workflow,
                order: index + 1,
            }));
            this.updateWorkflowOrder(updatedWorkflows).then(() => {
                console.log("Workflows order updated successfully");
            });
        },
    },
    watch: {
        workflows: {
            handler(newWorkflows) {
                if ((!newWorkflows || Object.keys(newWorkflows).length === 0) && !this.firstWorkflowLoaded) {
                    // Workflows yoksa yükle
                    this.firstWorkflowLoaded = true
                    this.getWorkFlows();
                    return;
                }

                const filtered = Object.values(newWorkflows).filter((workflow) => {
                    if (this.isRoot || this.isAdmin) {
                        return true;
                    }
                    const visibility = workflow.visibility || {};
                    const users = visibility.users || [];
                    const departments = visibility.departments || [];
                    const userSubjects = this.user.subjects || [];
                    const userId = this.user._id || null;

                    if (users.includes(userId) && departments.length === 0) {
                        return true;
                    } else if (departments.some((dept) => userSubjects.includes(dept)) && users.length === 0) {
                        return true;
                    } else if (users.includes(userId) || departments.some((dept) => userSubjects.includes(dept))) {
                        return true;
                    } else if (users.length === 0 && departments.length === 0) {
                        return true;
                    }
                    return false;
                });

                // Sadece değişiklik varsa güncelle
                if (JSON.stringify(this.localWorkflows.map(w => w._id)) !== JSON.stringify(filtered.map(w => w._id))) {
                    this.localWorkflows = filtered;
                }
            },
            immediate: true,
            deep: true,
        },
    },
    mounted() {
        // Sayfa açıldığında workflows'ları yükle
        if (Object.keys(this.workflows).length === 0) {
            this.getWorkFlows();
        }
    },
};
</script>

<style lang="scss" scoped>
.workflow-container {
    padding: 24px;
    background-color: #f5f7f9;
    min-height: 100vh;
}

.workflow-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    .workflow-title {
        font-size: 24px;
        font-weight: 600;
        color: #22272d;
    }

    div {
        .view-toggle-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 16px;
            background: #22272d;
            color: white;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
                background: darken(#22272d, 10%);
            }

            &.drag-active {
                background: #06a9f3;

                &:hover {
                    background: darken(#06a9f3, 10%);
                }
            }

            i {
                font-size: 20px;
            }
        }
    }

}

.table-view-container {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    overflow: hidden;

    .table-wrapper {
        overflow-x: auto;

        &::-webkit-scrollbar {
            height: 8px;
        }

        &::-webkit-scrollbar-track {
            background: #f1f1f1;
        }

        &::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;
        }
    }

    .workflow-table {
        width: 100%;
        border-collapse: collapse;

        th, td {
            padding: 16px;
            text-align: left;
        }

        th {
            background: #22272d;
            color: white;
            font-weight: 500;
        }

        tr {
            border-bottom: 1px solid #eee;

            &:hover {
                background: #f8f9fa;
            }
        }

        .actions-column {
            width: 280px;
        }

        .action-buttons {
            display: flex;
            gap: 8px;
            justify-content: flex-end;
        }

        .action-btn {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 6px 12px;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s ease;
            color: white;

            &.open {
                background: #06a9f3;

                &:hover {
                    background: darken(#06a9f3, 10%);
                }
            }

            &.edit {
                background: #22272d;

                &:hover {
                    background: darken(#22272d, 10%);
                }
            }

            &.delete {
                background: #dc3545;

                &:hover {
                    background: darken(#dc3545, 10%);
                }
            }

            i {
                font-size: 16px;
            }
        }
    }
}

.grid-view-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 16px;
    margin-top: 24px;
    width: 100%;
}

.draggable-container {
    display: grid;
    grid-template-columns: inherit;
    gap: inherit;
    width: 100%;
    grid-column: 1 / -1;
}

.workflow-drag {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
}
.workflow-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    position: relative;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    width: 100%;
    min-width: 280px;
    box-sizing: border-box;
    max-width: 200px;
    max-height: 200px;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    }

    &.drag-mode {
        cursor: move;
        border: 2px dashed #06a9f3;

        &:hover {
            background: #f0f8ff;
        }
    }

    &.add-card {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        background: #22272d;
        color: white;
        width: 100%;
        height: 100px;

        i {
            font-size: 32px;
        }

        span {
            font-size: 16px;
            font-weight: 500;
        }
    }
}

.workflow-card-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;

    .workflow-icon {
        width: 48px;
        height: 48px;
        background: #435162;
        padding: 9px;
        border-radius: 10px;
    }

    .workflow-card-title {
        font-size: 16px;
        font-weight: 500;
        text-align: center;
        margin: 0;
    }
}

.workflow-card-actions {
    position: absolute;
    top: 12px;
    right: 12px;

    .more-btn {
        background: transparent;
        border: none;
        padding: 4px;
        cursor: pointer;
        color: #666;

        &:hover {
            color: #333;
        }
    }
}

@media (max-width: 768px) {
    .workflow-container {
        padding: 16px;
    }

    .workflow-header {
        flex-direction: column;
        gap: 16px;
        align-items: flex-start;
    }

    .grid-view-container {
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .workflow-card {
        min-width: unset;
    }
}
</style>
